import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Link as MuiLink,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { authApi } from '../../services/authApi';
import { useAuthStore } from '../../stores/authStore';
import { ApiError } from '../../services/api';

/**
 * Password rules mirror the API's Zod schema: 10–200 chars. We add a
 * confirmPassword field on the client so a typo doesn't lock the user
 * out — the API itself only sees `email` + `password`.
 */
const RegisterSchema = z
  .object({
    email: z.string().min(1, 'Email is required').email('Please enter a valid email address.'),
    password: z
      .string()
      .min(10, 'Password must be at least 10 characters long.')
      .max(200, 'Password is too long.'),
    confirmPassword: z.string().min(1, 'Please re-enter your password.'),
  })
  .refine((v) => v.password === v.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords don’t match.',
  });

type RegisterFields = z.infer<typeof RegisterSchema>;

function registerErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.httpStatus === 409) {
      return 'An account with that email already exists. Try signing in instead.';
    }
    if (err.httpStatus === 400 && err.serverMessage) {
      return err.serverMessage;
    }
    if (err.httpStatus === 429) {
      return 'Too many attempts. Please wait a minute and try again.';
    }
  }
  return 'We couldn’t create your account. Please try again.';
}

export function RegisterPage() {
  const navigate = useNavigate();
  const signIn = useAuthStore((s) => s.signIn);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFields>({
    resolver: zodResolver(RegisterSchema),
    defaultValues: { email: '', password: '', confirmPassword: '' },
  });

  async function onSubmit(values: RegisterFields) {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const bundle = await authApi.register({ email: values.email, password: values.password });
      signIn(bundle);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setSubmitError(registerErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box sx={{ maxWidth: 480, mx: 'auto' }}>
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 4 } }}>
        <Typography variant="h1" component="h1" sx={{ fontSize: '1.6rem', mb: 1 }}>
          Create an account
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Create alerts, save searches, and build a watchlist. Round Recon never sells your data and
          never processes ammunition transactions.
        </Typography>

        {submitError ? (
          <Alert severity="error" role="alert" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        ) : null}

        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          aria-label="Create account"
        >
          <Stack spacing={2}>
            <TextField
              label="Email"
              type="email"
              autoComplete="email"
              required
              fullWidth
              {...register('email')}
              error={Boolean(errors.email)}
              helperText={errors.email?.message ?? ' '}
              inputProps={{ 'aria-invalid': Boolean(errors.email) }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Password"
              type="password"
              autoComplete="new-password"
              required
              fullWidth
              {...register('password')}
              error={Boolean(errors.password)}
              helperText={errors.password?.message ?? 'At least 10 characters.'}
              inputProps={{ 'aria-invalid': Boolean(errors.password) }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Confirm password"
              type="password"
              autoComplete="new-password"
              required
              fullWidth
              {...register('confirmPassword')}
              error={Boolean(errors.confirmPassword)}
              helperText={errors.confirmPassword?.message ?? ' '}
              inputProps={{ 'aria-invalid': Boolean(errors.confirmPassword) }}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={submitting}
              aria-busy={submitting}
            >
              {submitting ? 'Creating account…' : 'Create account'}
            </Button>
          </Stack>
        </Box>

        <Typography variant="body2" sx={{ mt: 3 }}>
          Already have an account?{' '}
          <MuiLink component={RouterLink} to="/login">
            Sign in
          </MuiLink>
          .
        </Typography>
      </Paper>
    </Box>
  );
}

export default RegisterPage;
